import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { 
  FaImage,
  FaProjectDiagram,
  FaTable
} from 'react-icons/fa';
import './gallery.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';

function Gallery() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Define gallery sections with icons and descriptions
  const gallerySections = [
    {
      title: 'Step 1',
      color: '#FFB5B5', // # Pastel red
      subsections: [
        {
          title: 'Images',
          icon: <FaImage className="ext_gal_section-icon" />,
          description: 'High-quality medical images for Step 1'
        },
        {
          title: 'Flowcharts',
          icon: <FaProjectDiagram className="ext_gal_section-icon" />,
          description: 'Visual pathways and algorithms'
        },
        {
          title: 'Tables',
          icon: <FaTable className="ext_gal_section-icon" />,
          description: 'Comparative tables and quick references'
        }
      ]
    },
    {
      title: 'Step 2',
      color: '#B5D8FF', // # Pastel blue
      subsections: [
        {
          title: 'Images',
          icon: <FaImage className="ext_gal_section-icon" />,
          description: 'Clinical images and radiographs'
        },
        {
          title: 'Flowcharts',
          icon: <FaProjectDiagram className="ext_gal_section-icon" />,
          description: 'Clinical decision trees'
        },
        {
          title: 'Tables',
          icon: <FaTable className="ext_gal_section-icon" />,
          description: 'Clinical comparison tables'
        }
      ]
    }
  ];

  return (
    <div className={`ext_gal_page-container ${isDarkMode ? 'ext_gal_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="ext_gal_main-content">
        <div className="ext_gal_header">
          <h1>Visual Gallery</h1>
          <p>Comprehensive collection of medical images, flowcharts, and tables</p>
        </div>

        <div className="ext_gal_sections-grid">
          {gallerySections.map((section, index) => (
            <div 
              key={index}
              className="ext_gal_section-column"
              style={{ '--section-color': section.color }}
            >
              <h2 className="ext_gal_section-title">{section.title}</h2>
              <div className="ext_gal_subsections-grid">
                {section.subsections.map((subsection, subIndex) => (
                  <div 
                    key={subIndex}
                    className="ext_gal_subsection-card"
                  >
                    <div className="ext_gal_icon-container">
                      {subsection.icon}
                    </div>
                    <h3>{subsection.title}</h3>
                    <p>{subsection.description}</p>
                    <div className="ext_gal_coming-soon">Coming Soon</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default Gallery; 